<script setup>

import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  modelValue : { type: [Array, String, null], default: null },
  options    : { type: Array, default: () => [] },   // [{ value, label }]
  multiple   : { type: Boolean, default: false },
  search     : { type: Boolean, default: false },
  title      : { type: String, default: 'Select…' },
  value      : { type: String, default: 'value' },
  label      : { type: String, default: 'label' }
});

const emit = defineEmits(['update:modelValue', 'change']);

const open  = ref(false);
const query = ref('');
const root  = ref(null);

const selected = computed(() => {
  if (props.multiple) return Array.isArray(props.modelValue) ? props.modelValue : [];
  return props.modelValue != null && props.modelValue !== '' ? [props.modelValue] : [];
});

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return props.options;
  return props.options.filter(o => String(o[props.label]).toLowerCase().includes(q) || String(o[props.value]).toLowerCase().includes(q));
});

const label = computed(() => {
  if (!selected.value.length) return props.title;
  if (props.multiple) {
    if (selected.value.length > 2) return `${selected.value.length} selected`;
    return props.options.filter(o => selected.value.includes(o[props.value])).map(o => o[props.label]).join(', ');
  }
  const hit = props.options.find(o => o[props.value] === props.modelValue);
  return hit ? hit[props.label] : props.title;
});

const isSelected = (value) => selected.value.includes(value);

function toggle(value) {
  if (props.multiple) {
    const next = isSelected(value)
      ? selected.value.filter(v => v !== value)
      : [...selected.value, value];
    emit('update:modelValue', next);
    emit('change', next);
  } else {
    const next = props.modelValue === value ? '' : value;
    emit('update:modelValue', next);
    emit('change', next);
    open.value = false;
  }
}

function clear(e) {
  e.stopPropagation();
  const next = props.multiple ? [] : '';
  emit('update:modelValue', next);
  emit('change', next);
}

function onDocClick(e) {
  if (root.value && !root.value.contains(e.target)) open.value = false;
}

onMounted(() => document.addEventListener('click', onDocClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocClick));
</script>

<template>
  <div class="ti-select" :class="{ open }" ref="root">
    <button type="button" class="ti-select-toggle" @click="open = !open">
      <span class="ti-select-label" :class="{ placeholder: !selected.length }">{{ label }}</span>
      <span v-if="selected.length" class="ti-select-clear" @click="clear">×</span>
      <svg class="ti-select-caret" width="10" height="10" viewBox="0 0 10 10"><path d="M1 3l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>

    <div v-show="open" class="ti-select-menu">
      <div v-if="search" class="ti-select-search">
        <input v-model="query" type="text" placeholder="Search…" @click.stop>
      </div>
      <ul class="ti-select-list">
        <li
          v-for="o in filtered"
          :key="o[props.value]"
          class="ti-select-option"
          :class="{ active: isSelected(o[props.value]) }"
          @click="toggle(o[props.value])"
        >
          <span v-if="multiple" class="ti-select-check">{{ isSelected(o[props.value]) ? '✓' : '' }}</span>
          {{ o[props.label] }}
        </li>
        <li v-if="!filtered.length" class="ti-select-empty">No matches</li>
      </ul>
    </div>
  </div>
</template>

<style scoped>

.ti-select { position: relative; }
.ti-select-toggle {
  width: 100%;
  display: flex; align-items: center; gap: 8px;
  padding: 10px 12px;
  background: var(--paper);
  border: 1px solid var(--line-2);
  border-radius: var(--r-sm);
  font: inherit; font-size: 14px; color: var(--ink);
  cursor: pointer; text-align: left;
  transition: border-color .15s ease;
}
.ti-select.open .ti-select-toggle,
.ti-select-toggle:hover { border-color: var(--brand-400); }
.ti-select-label { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ti-select-label.placeholder { color: var(--muted); }
.ti-select-clear {
  color: var(--muted); font-size: 16px; line-height: 1;
  padding: 0 2px; border-radius: 3px;
}
.ti-select-clear:hover { color: var(--ink); background: var(--bg); }
.ti-select-caret { color: var(--muted); transition: transform .15s ease; flex-shrink: 0; }
.ti-select.open .ti-select-caret { transform: rotate(180deg); }

.ti-select-menu {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 40;
  background: var(--paper);
  border: 1px solid var(--line-2);
  border-radius: var(--r-sm);
  box-shadow: 0 8px 24px rgba(20,40,70,.10);
  overflow: hidden;
}
.ti-select-search { padding: 8px; border-bottom: 1px solid var(--line); }
.ti-select-search input {
  width: 100%; padding: 8px 10px; font: inherit; font-size: 13px;
  border: 1px solid var(--line-2); border-radius: var(--r-sm); background: var(--bg);
}
.ti-select-search input:focus { outline: none; border-color: var(--brand-400); }
.ti-select-list { list-style: none; margin: 0; padding: 4px; max-height: 260px; overflow-y: auto; }
.ti-select-option {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px; font-size: 13px; border-radius: var(--r-sm);
  cursor: pointer;
}
.ti-select-option:hover { background: var(--bg); }
.ti-select-option.active { background: var(--brand-50); color: var(--brand-700); font-weight: 600; }
.ti-select-check { width: 14px; color: var(--brand-600); flex-shrink: 0; }
.ti-select-empty { padding: 10px; font-size: 13px; color: var(--muted); text-align: center; }

</style>
